import type { CityConfig } from "../city-template/types";

export const parkCityUTConfig: CityConfig = {
  city: "Park City",
  state: "Utah",
  stateAbbr: "UT",
  slug: "park-city",
  route: "/cities/park-city",
  title: "Emergency Passport Park City, UT | Expedited Passport Services | Fast Passport Center",
  metaDescription: "Emergency and expedited passport services in Park City, UT — as fast as 24 hours. Serving Summit County and the Wasatch Back region. A+ BBB rated registered State Dept. courier serving Sundance Film Festival travelers and resort communities. No office visit required.",
  h1: "Emergency & Expedited Passport Services in Park City, UT",
  heroSubtitle: "Sundance Film Festival trip to Cannes and your passport expired? Last-minute ski trip to the Swiss Alps and your documents are not ready? Business conference in London departing from Salt Lake in 5 days? We help Park City residents get their passports fast — as fast as 24 hours. A+ BBB rated registered State Dept. courier. Best price guarantee. No office visit required.",
  heroImageUrl: "https://readdy.ai/api/search-image?query=Panoramic%20view%20of%20Park%20City%20Utah%20historic%20Main%20Street%20with%20colorful%20Victorian%20buildings%20mountain%20resort%20town%20atmosphere%20Wasatch%20Mountains%20snow%20capped%20peaks%20in%20background%20winter%20scene%20blue%20sky%20professional%20landscape%20photography%20luxury%20ski%20destination%20aesthetic&width=1800&height=700&seq=park-city-ut-hero-2026&orientation=landscape",
  heroImageAlt: "Park City Utah historic Main Street with Wasatch Mountains backdrop in winter",
  geoPosition: "40.6461;-111.4980",
  geoPlacename: "Park City, Utah",
  regionalAgency: {
    name: "Denver Passport Agency",
    address: "3151 S Vaughn Way, Suite 600, Aurora, CO 80014",
    distance: "Located in Aurora, CO — approximately 520 miles east of Park City via I-80/I-25",
  },
  nearbyCitiesZips: [
    { name: "Park City", zips: "84060, 84098" },
    { name: "Heber City", zips: "84032" },
    { name: "Kamas", zips: "84036" },
    { name: "Midway", zips: "84049" },
    { name: "Coalville", zips: "84017" },
    { name: "Oakley", zips: "84055" },
    { name: "Francis", zips: "84036" },
    { name: "Snyderville", zips: "84098" },
    { name: "Kimball Junction", zips: "84098" },
    { name: "Deer Valley", zips: "84060" },
    { name: "Alta", zips: "84092" },
    { name: "Brighton", zips: "84121" },
    { name: "Salt Lake City", zips: "84101" },
    { name: "Morgan", zips: "84050" },
    { name: "Evanston", zips: "82930" },
  ],
  benefits: [
    { icon: "ri-time-line", title: "24–48 Hour Processing", desc: "Emergency courier submission for urgent Summit County departures" },
    { icon: "ri-shield-check-line", title: "Registered Courier", desc: "U.S. Department of State approved passport expediting" },
    { icon: "ri-user-line", title: "Dedicated Agent", desc: "A real person assigned to your case from start to finish" },
    { icon: "ri-map-pin-2-line", title: "Wasatch Back Region", desc: "Serving Park City, Heber City, Kamas, and all of Summit County" },
  ],
  internalLinks: [
    { name: "Expedited passport services in Salt Lake City, UT", route: "/cities/salt-lake-city" },
    { name: "Fast passport renewal in Ogden, UT", route: "/cities/ogden" },
    { name: "Emergency passport help in Provo, UT", route: "/cities/provo" },
    { name: "Urgent passport services in Orem, UT", route: "/cities/orem" },
    { name: "Same-day passport help in Denver, CO", route: "/cities/denver" },
    { name: "Passport courier services in St. George, UT", route: "/cities/st-george" },
  ],
  fpcOfficeNote: "Fast Passport Center serves Park City remotely via secure courier. We do not have a walk-in office in Park City — our registered couriers hand-deliver your documents directly to U.S. Department of State processing facilities. This is how we achieve 24–48 hour turnaround for Summit County and the Wasatch Back.",
  fpcOfficeAddress: "299 S Main St, Salt Lake City, Utah 84111",
  fpcOfficeDistance: "Serving all of Summit County and the Wasatch Back region including Wasatch and Morgan counties",
  serviceAreaIntro: "From the iconic Main Street and world-class ski resorts of Park City to the alpine charm of Deer Valley, from the Swiss-inspired town of Midway to the ranching community of Kamas, from the historic courthouse of Coalville to the mountain retreats of Oakley and Francis — Fast Passport Center serves the entire Wasatch Back and Summit County region. We regularly help travelers from Park City, Heber City, and surrounding mountain communities get their passports fast without the 520-mile drive to Denver.",
  nearbyCitiesIntro: "We serve all Park City neighborhoods and the surrounding Wasatch Back area including Heber City, Kamas, Midway, Coalville, Oakley, Francis, Kimball Junction, and Deer Valley. If you are anywhere in Summit County or Wasatch County, we have you covered.",
  howItWorksIntro: "Most Park City travelers complete everything online and ship documents overnight. No need to drive to Denver or navigate Salt Lake City — we handle the courier submission for you.",
  mistakesIntro: "Park City travelers often face an 8-hour drive to the nearest regional passport agency in Denver. Here are the most common mistakes that cause delays.",
  testimonialsIntro: "Park City and Summit County travelers trust us when their departure is days away. Here is what some of them have to say.",
  faqIntro: "Have questions about expedited passports in Park City? Here are the most common ones we hear from Wasatch Back travelers.",
  statePageRoute: "/states/utah",
  statePageName: "Utah",
  dmvWebsite: "https://dmv.utah.gov",
  dmvLabel: "Utah DMV — Division of Motor Vehicles",
  stickyNavLinks: [
    { label: "Processing", href: "#processing-options" },
    { label: "Services", href: "#services" },
    { label: "Service Area", href: "#service-area" },
    { label: "Facilities", href: "#acceptance-facilities" },
    { label: "Comparison", href: "#comparison" },
    { label: "How It Works", href: "#how-it-works" },
    { label: "FAQ", href: "#faq" },
    { label: "Resources", href: "#local-resources" },
  ],
  processingOptions: [
    {
      speed: "24",
      unit: "Hours*",
      label: "Regional Agency Processing",
      tag: "Emergency Only",
      tagColor: "bg-gray-100 text-gray-500",
      features: [
        "Appointment required — no walk-ins allowed",
        "Life-or-death emergencies only",
        "Bereavement or medical crisis",
        "Expect to wait all day or return next day",
      ],
      highlight: false,
      emergency: true,
    },
    {
      speed: "2–9",
      unit: "Business Days",
      label: "Fast Passport Center",
      tag: "Most Popular",
      tagColor: "bg-[#F6C344] text-[#1A365D]",
      features: [
        "Registered U.S. Department of State Courier",
        "Reserved agency submission",
        "Predictable turnaround",
        "Ideal for upcoming trips",
        "Document review included",
        "Dedicated agent assigned to your case",
        "Daily status updates — never left guessing",
        "Not affected by government shutdowns",
      ],
      highlight: true,
    },
    {
      speed: "3–5",
      unit: "Weeks",
      label: "Fast Passport Center",
      tag: "Standard Expedited",
      tagColor: "bg-[#1A365D]/10 text-[#1A365D]",
      features: [
        "Registered U.S. Department of State Courier",
        "Reserved agency submission",
        "Faster than self-service",
        "Good for planned travel",
        "Document review included",
        "Dedicated agent assigned to your case",
        "Daily status updates — never left guessing",
        "Not affected by government shutdowns",
      ],
      highlight: false,
      smartSaver: true,
    },
    {
      speed: "1–2",
      unit: "Months",
      label: "Self-Service (Post Office)",
      tag: "Slowest",
      tagColor: "bg-gray-100 text-gray-400",
      features: [
        "No courier access — standard mail only",
        "Workload & wait times shift with shutdowns",
        "In-person clerk appointment required",
        "No document review — errors cause rejection",
        "No dedicated agent — you are on your own",
        "Updates only by physical mail letter",
        "No phone or email contact for your case",
        "Can't upgrade speed after submission",
        "Not suitable if travel is within 10 weeks",
      ],
      highlight: false,
      muted: true,
    },
  ],
  services: [
    {
      icon: "ri-flashlight-line",
      title: "Emergency Passport",
      description: "For Park City travelers with immediate departure needs — as fast as 24 hours for urgent international flights.",
      bullets: ["24–48 hour turnaround available", "Registered courier hand-delivery", "Flight verification support"],
      badge: "Most Popular",
      link: "https://fastpassportcenter.com/checkout/?passport=new-passport",
    },
    {
      icon: "ri-passport-line",
      title: "Passport Renewal",
      description: "Fast renewal for adults with a passport issued within the last 15 years at age 16 or older.",
      bullets: ["No clerk visit required for most", "Mail-in or courier options", "Lost passport replacement available"],
      badge: null,
      link: "https://fastpassportcenter.com/checkout/?passport=passport-renewal",
    },
    {
      icon: "ri-add-circle-line",
      title: "New Passport",
      description: "First-time passport application guidance with acceptance clerk appointment support.",
      bullets: ["Acceptance facility locator", "Form DS-11 assistance", "Photo requirement guidance"],
      badge: null,
      link: "https://fastpassportcenter.com/checkout/?passport=new-passport",
    },
    {
      icon: "ri-alert-line",
      title: "Lost or Stolen Passport",
      description: "Passport replacement with DS-64 form guidance and police report documentation.",
      bullets: ["DS-64 form assistance", "Identity verification support", "Emergency replacement options"],
      badge: "Urgent",
      link: "https://fastpassportcenter.com/checkout/?passport=lost-stolen-passport",
    },
    {
      icon: "ri-parent-line",
      title: "Child Passport",
      description: "Passport services for minors under 16 with parental consent and acceptance clerk support.",
      bullets: ["DS-11 form for minors", "Parental consent guidance", "Both parents presence rules"],
      badge: null,
      link: "https://fastpassportcenter.com/checkout/?passport=child-passport",
    },
    {
      icon: "ri-file-edit-line",
      title: "Name Change",
      description: "Passport correction and name change service after marriage, divorce, or legal changes.",
      bullets: ["DS-5504 or DS-82 guidance", "Marriage certificate support", "Court order documentation"],
      badge: null,
      link: "https://fastpassportcenter.com/checkout/?passport=name-change",
    },
    {
      icon: "ri-bank-card-line",
      title: "Passport Card",
      description: "Wallet-sized U.S. Passport Card for land and sea travel to Canada, Mexico, Bermuda, and the Caribbean.",
      bullets: ["REAL ID compliant alternative", "Fits in your wallet", "Land/sea border crossing approved"],
      badge: null,
      link: "https://fastpassportcenter.com/checkout/?passport=passport-card",
    },
    {
      icon: "ri-error-warning-line",
      title: "Damaged Passport",
      description: "Replacement for water-damaged, torn, or otherwise mutilated passports that are no longer valid for travel.",
      bullets: ["Damage assessment guidance", "DS-11 replacement process", "Supporting documentation help"],
      badge: null,
      link: "https://fastpassportcenter.com/checkout/?passport=damaged-passport",
    },
    {
      icon: "ri-file-copy-line",
      title: "Second Passport",
      description: "Obtain a second valid U.S. passport for frequent international travelers who need overlapping visas.",
      bullets: ["For frequent business travelers", "Overlapping visa support", "Expedited processing available"],
      badge: null,
      link: "https://fastpassportcenter.com/checkout/?passport=second-passport",
    },
  ],
  servicesSchema: [
    { name: "Emergency Passport", description: "Urgent passport processing for travelers with immediate departure needs, as fast as 24 hours." },
    { name: "Passport Renewal", description: "Fast passport renewal for U.S. citizens with a passport issued within the last 15 years at age 16 or older." },
    { name: "New Passport", description: "First-time passport application assistance with step-by-step guidance through the acceptance clerk process." },
    { name: "Lost or Stolen Passport", description: "Passport replacement service for lost or stolen passports, including DS-64 form guidance." },
    { name: "Child Passport", description: "Passport services for minors under 16, with parental consent guidance and acceptance clerk support." },
    { name: "Name Change", description: "Passport correction and name change service after marriage, divorce, or legal name changes." },
    { name: "Passport Card", description: "Wallet-sized U.S. Passport Card for land and sea travel to Canada, Mexico, Bermuda, and the Caribbean." },
    { name: "Damaged Passport", description: "Replacement for water-damaged, torn, or mutilated passports requiring DS-11 replacement." },
    { name: "Second Passport", description: "Second valid U.S. passport for frequent international travelers with overlapping visa needs." },
  ],
  testimonials: [
    {
      name: "Tyler R.",
      location: "Park City, UT",
      rating: 5,
      text: "Sundance to Cannes in 5 days — my passport had expired and I was panicking. Fast Passport Center got it done in 30 hours. I was on that flight. Unbelievable service.",
      service: "Passport Renewal",
    },
    {
      name: "Catherine D.",
      location: "Deer Valley, UT",
      rating: 5,
      text: "Our family ski trip to Switzerland was booked and our son's passport was expired. Fast Passport Center expedited a child passport in 3 days. Deer Valley to the Alps — incredible.",
      service: "Child Passport",
    },
    {
      name: "James H.",
      location: "Heber City, UT",
      rating: 5,
      text: "Medical emergency in Tokyo. Fast Passport Center processed my emergency passport in 26 hours. My dedicated agent called me every step of the way. Life-saving service.",
      service: "Emergency Passport",
    },
  ],
  facilities: [
    {
      id: "park-city-ut-1",
      name: "Park City Post Office",
      type: "USPS Passport Acceptance Facility",
      address: "2100 Park Ave",
      city: "Park City",
      state: "UT",
      zip: "84060",
      phone: "(435) 649-2111",
      hours: "Mon–Fri: 9:00 AM – 4:00 PM",
      distance: "Downtown Park City",
      website: "https://tools.usps.com/find-location.htm",
      websiteLabel: "USPS Location Finder",
      notes: "Primary Park City facility. Appointments strongly recommended. Call ahead to confirm passport acceptance hours.",
      icon: "ri-mail-line",
    },
    {
      id: "park-city-ut-2",
      name: "Summit County Clerk",
      type: "County Clerk Passport Acceptance",
      address: "60 N Main St",
      city: "Coalville",
      state: "UT",
      zip: "84017",
      phone: "(435) 336-3261",
      hours: "Mon–Fri: 8:00 AM – 5:00 PM",
      distance: "20 miles east of Park City",
      website: "https://www.summitcounty.org/160/Clerk",
      websiteLabel: "Summit County Clerk",
      notes: "Accepts passport applications. Good alternative to post office. Appointment recommended.",
      icon: "ri-government-line",
    },
    {
      id: "park-city-ut-3",
      name: "Heber City Post Office",
      type: "USPS Passport Acceptance Facility",
      address: "125 E 100 N",
      city: "Heber City",
      state: "UT",
      zip: "84032",
      phone: "(435) 654-2111",
      hours: "Mon–Fri: 9:00 AM – 4:00 PM",
      distance: "17 miles south of Park City",
      website: "https://tools.usps.com/find-location.htm",
      websiteLabel: "USPS Location Finder",
      notes: "Serves Wasatch County. Full passport services available.",
      icon: "ri-mail-line",
    },
    {
      id: "park-city-ut-4",
      name: "Kimball Junction Post Office",
      type: "USPS Passport Acceptance Facility",
      address: "6400 N Business Park Loop Rd",
      city: "Park City",
      state: "UT",
      zip: "84098",
      phone: "(435) 615-2111",
      hours: "Mon–Fri: 9:00 AM – 4:00 PM",
      distance: "Kimball Junction area, Park City",
      website: "https://tools.usps.com/find-location.htm",
      websiteLabel: "USPS Location Finder",
      notes: "Convenient for Kimball Junction and Snyderville residents. Call ahead for passport hours.",
      icon: "ri-mail-line",
    },
  ],
  comparisonRows: [
    { feature: "Appointment Needed", agency: "Yes — call NPIC, limited slots", fpc: "No — we handle everything for you" },
    { feature: "Processing Speed", agency: "Same-day if approved (rare)", fpc: "24–48 hours guaranteed for emergency" },
    { feature: "Document Review", agency: "No — rejected if wrong", fpc: "Yes — we check before submission" },
    { feature: "Travel to Agency", agency: "Yes — 8 hour drive to Denver", fpc: "No — ship from home or office" },
    { feature: "Agent Support", agency: "No — government clerk only", fpc: "Yes — dedicated agent assigned" },
    { feature: "Status Updates", agency: "No — no tracking provided", fpc: "Yes — daily email updates" },
    { feature: "Guaranteed Timing", agency: "No — subject to availability", fpc: "Yes — contractual turnaround times" },
  ],
  mistakes: [
    {
      icon: "ri-image-line",
      title: "Wrong Photo Size or Background",
      description: "Park City photo shops sometimes use artistic or non-standard backgrounds. Passport photos must be exactly 2×2 inches with a white or off-white background.",
    },
    {
      icon: "ri-file-list-line",
      title: "Incomplete DS-11 Form",
      description: "Missing signatures or using the wrong form type are the #1 reasons for rejection at Summit County acceptance facilities.",
    },
    {
      icon: "ri-money-dollar-circle-line",
      title: "Wrong Payment Amount",
      description: "Fees change frequently. Many Park City applicants show up with incorrect payment and have to reschedule their appointment.",
    },
    {
      icon: "ri-calendar-close-line",
      title: "No Proof of Travel",
      description: "Without flight confirmation or travel itinerary, the Denver regional agency will turn you away for emergency appointments.",
    },
    {
      icon: "ri-parent-line",
      title: "Missing Parental Consent",
      description: "For minors, both parents must appear or submit notarized consent. This trips up many Summit County families at the clerk.",
    },
    {
      icon: "ri-time-line",
      title: "Waiting Too Long to Start",
      description: "Park City travelers often assume standard processing will be fine. It takes 8–11 weeks. Start expedited at least 3 weeks before travel.",
    },
  ],
  steps: [
    { number: "1", icon: "ri-file-list-line", title: "Apply Online", description: "Complete your application on our secure website in under 10 minutes." },
    { number: "2", icon: "ri-mail-send-line", title: "Ship Documents", description: "We send you a prepaid overnight label. Drop your documents at any UPS location in Park City." },
    { number: "3", icon: "ri-shield-check-line", title: "We Review", description: "Our team checks every document for accuracy before hand-delivering to the State Department." },
    { number: "4", icon: "ri-passport-line", title: "Processing", description: "Your passport is processed at a U.S. Department of State facility using our registered courier slots." },
    { number: "5", icon: "ri-truck-line", title: "Delivery", description: "We overnight your new passport back to your Park City address — trackable every step of the way." },
  ],
  faqs: [
    { q: "Can I get an emergency passport in Park City before international travel?", a: "Yes. Fast Passport Center provides emergency passport services in Park City for travelers with urgent international flights. Our registered couriers hand-deliver your documents directly to U.S. Department of State facilities, achieving 24–48 hour turnaround — without the 8-hour drive to Denver." },
    { q: "How fast can I renew my passport in Park City?", a: "Fast Passport Center offers fast passport renewal in Park City in as little as 24–48 hours through our emergency courier service. For less urgent travel, our 3–5 day expedited option provides the best value. Both options include full document review and a dedicated agent." },
    { q: "What is the closest passport agency to Park City?", a: "The nearest regional passport agency to Park City is the Denver Passport Agency, approximately 520 miles east via I-80/I-25. For urgent processing without the long drive, Fast Passport Center's registered courier service achieves 24–48 hour turnaround from Park City without any in-person agency visit required." },
    { q: "Can I get a same-day passport appointment near Park City?", a: "Same-day passport appointments are only available at the Denver Regional Passport Agency — approximately 8 hours from Park City — for documented life-or-death emergencies. For all other urgent travel, Fast Passport Center's 24–48 hour emergency courier processing is the fastest guaranteed option for Summit County and Wasatch Back residents." },
    { q: "Do you serve the entire Summit County region?", a: "Absolutely. Fast Passport Center serves the entire Wasatch Back and Summit County region including Park City, Heber City, Kamas, Midway, Coalville, and surrounding areas. Our courier service works seamlessly from any address in Summit County or Wasatch County." },
    { q: "Can I expedite a child passport in Park City?", a: "Yes. Fast Passport Center expedites child passports in Park City for minors under 16. Both parents must appear or submit notarized consent, along with the child's birth certificate and both parents' IDs. We walk Summit County families through the entire process." },
    { q: "What should I do if I lost my passport before a trip?", a: "If you lost your passport before a trip, contact Fast Passport Center immediately. We provide lost passport replacement services in Park City with DS-64 form guidance and identity verification support. Our emergency courier service can get your replacement passport in 24–48 hours." },
    { q: "Do I need to drive to the Denver Passport Agency?", a: "No. With Fast Passport Center, you complete everything online and ship your documents overnight. Our couriers handle the in-person delivery to State Department facilities. You only need to visit a local acceptance clerk if you're a first-time applicant or need a name change." },
  ],
  photoLocations: [
    { name: "Walmart Supercenter — Kimball Junction", address: "6545 N Landmark Dr, Park City, UT 84098", hours: "Mon–Sun: 6AM–11PM" },
    { name: "Walgreens — Park City", address: "210 E 1300 S, Park City, UT 84060", hours: "Mon–Sun: 7AM–11PM" },
    { name: "Smith's — Heber City", address: "550 N Main St, Heber City, UT 84032", hours: "Mon–Sun: 6AM–11PM" },
    { name: "Walmart Supercenter — Heber City", address: "1274 S US-189, Heber City, UT 84032", hours: "Mon–Sun: 6AM–11PM" },
  ],
  dmvLocations: [
    {
      name: "Utah DMV — Park City",
      address: "1885 Ute Blvd, Park City, UT 84098",
      hours: "Mon–Fri: 7AM–5PM",
      note: "Driver license and REAL ID services. Appointment recommended. Located in Kimball Junction.",
      featured: true,
    },
    {
      name: "Utah DMV — Heber City",
      address: "69 N 600 W, Heber City, UT 84032",
      hours: "Mon–Fri: 7AM–5PM",
      note: "Serves Wasatch County. REAL ID available.",
      featured: false,
    },
  ],
  airports: [
    {
      code: "SLC",
      name: "Salt Lake City International Airport",
      distance: "35 miles west of Park City",
      address: "776 N Terminal Dr, Salt Lake City, UT 84122",
      note: "Major international airport with direct flights worldwide. About 35 minutes from Park City via I-80.",
      flightUrl: "https://www.slcairport.com/",
      flightLabel: "SLC Airport",
      featured: true,
    },
    {
      code: "PVU",
      name: "Provo Municipal Airport",
      distance: "50 miles south of Park City",
      address: "3421 Mike Jense Pkwy, Provo, UT 84601",
      note: "Regional airport with limited commercial service via Allegiant and Breeze Airways. About 1 hour from Park City.",
      flightUrl: "https://www.provo.org/city-services/airport",
      flightLabel: "PVU Airport",
      featured: false,
    },
  ],
};